import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { PROVINCES, TRADE_CATEGORIES, URGENCIES } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";

export default function PostJobPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    title: "", description: "", province: "", city: "", category: "", urgency: "Flexible", photos: [],
  });
  const [loading, setLoading] = useState(false);

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center">
        <h1 className="font-display font-bold text-2xl text-slate-900 mb-2">Sign in to post a job</h1>
        <p className="text-slate-600 mb-6">Create a free homeowner account to get quotes from Canadian pros.</p>
        <div className="flex gap-2 justify-center">
          <Button data-testid="post-need-login" onClick={() => nav("/login")} variant="outline" className="border-blue-900 text-blue-900">Sign In</Button>
          <Button data-testid="post-need-register" onClick={() => nav("/register")} className="bg-orange-600 hover:bg-orange-700 text-white">Sign Up</Button>
        </div>
      </div>
    );
  }

  if (user.role !== "homeowner") {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center">
        <h1 className="font-display font-bold text-2xl text-slate-900 mb-2">Homeowner accounts only</h1>
        <p className="text-slate-600">Only homeowners can post jobs. Browse the job board to quote on work.</p>
      </div>
    );
  }

  const set = (k, v) => setForm({ ...form, [k]: v });

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5 - form.photos.length);
    const promises = files.map(
      (f) => new Promise((resolve) => {
        const r = new FileReader();
        r.onload = () => resolve({ id: `${Date.now()}-${Math.random().toString(36).slice(2)}`, data: r.result });
        r.readAsDataURL(f);
      })
    );
    const newPhotos = await Promise.all(promises);
    set("photos", [...form.photos, ...newPhotos].slice(0, 5));
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/jobs", { ...form, photos: form.photos.map((p) => p.data) });
      toast.success("Job posted!");
      nav(`/jobs/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to post");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <h1 className="font-display font-bold text-3xl text-slate-900 mb-2">Post a Job</h1>
      <p className="text-slate-600 mb-8">Tell us about your project. You&apos;ll receive quotes from local pros.</p>
      <Card className="border-slate-200">
        <CardContent className="p-6">
          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label>Trade category <span className="text-orange-600">*</span></Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="post-category" className="mt-1.5"><SelectValue placeholder="Select a trade…" /></SelectTrigger>
                <SelectContent>{TRADE_CATEGORIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="title">Job title <span className="text-orange-600">*</span></Label>
              <Input id="title" data-testid="post-title" value={form.title} onChange={(e) => set("title", e.target.value)} required placeholder="e.g. Replace kitchen faucet" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="desc">Description <span className="text-orange-600">*</span></Label>
              <Textarea id="desc" data-testid="post-description" rows={5} value={form.description} onChange={(e) => set("description", e.target.value)} required placeholder="Describe the job in detail…" className="mt-1.5" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Province <span className="text-orange-600">*</span></Label>
                <Select value={form.province} onValueChange={(v) => set("province", v)}>
                  <SelectTrigger data-testid="post-province" className="mt-1.5"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>{PROVINCES.map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="city">City <span className="text-orange-600">*</span></Label>
                <Input id="city" data-testid="post-city" value={form.city} onChange={(e) => set("city", e.target.value)} required placeholder="e.g. Toronto" className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label>Urgency <span className="text-orange-600">*</span></Label>
              <Select value={form.urgency} onValueChange={(v) => set("urgency", v)}>
                <SelectTrigger data-testid="post-urgency" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{URGENCIES.map((u) => (<SelectItem key={u} value={u}>{u}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Photos <span className="text-xs text-slate-500 font-normal">(optional, up to 5)</span></Label>
              <label className="mt-1.5 flex items-center justify-center h-24 rounded-md border border-dashed border-slate-300 cursor-pointer hover:bg-slate-50 transition-colors">
                <Upload className="w-4 h-4 mr-2 text-slate-500" />
                <span className="text-sm text-slate-600">Click to upload images</span>
                <input data-testid="post-photos" type="file" multiple accept="image/*" className="sr-only" onChange={onFiles} />
              </label>
              {form.photos.length > 0 && (
                <div className="grid grid-cols-5 gap-2 mt-3">
                  {form.photos.map((p) => (
                    <div key={p.id} className="relative">
                      <img src={p.data} alt="" className="aspect-square object-cover rounded-md border border-slate-200" />
                      <button type="button" onClick={() => set("photos", form.photos.filter((x) => x.id !== p.id))} className="absolute -top-1 -right-1 w-5 h-5 grid place-items-center rounded-full bg-slate-900 text-white">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Button
              type="submit"
              data-testid="post-submit"
              disabled={loading || !form.title || !form.description || !form.province || !form.city || !form.category}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white h-11"
            >
              {loading ? "Posting…" : "Post Job"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
